import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { createGig, updateGig } from '../../../services/gig.service';
import { toast } from 'react-toastify';

const PACKAGES = ['basic', 'standard', 'advanced', 'pro', 'premium'];

// Fields required for EVERY package that is touched (at least basic is always required)
const REQUIRED_FIELDS = ['title', 'description', 'deliveryDays', 'price'];
const FIELD_LABELS = {
  title: 'Title',
  description: 'Description',
  deliveryDays: 'Delivery Timeframe',
  price: 'Price ($)',
};

const Step3 = ({ formData, setFormData, setCurrentStep, isEditMode = false, gigId = null }) => {
  const navigate = useNavigate();
  const [editingField, setEditingField] = useState(null);
  const [errors, setErrors] = useState({});

  // ── Validation ──────────────────────────────────────────────────────────────
  const validate = () => {
    const newErrors = {};

    // Determine which packages need to be validated:
    // - Basic is ALWAYS required
    // - Any other package is required only if the user has partially filled it
    const packagesToValidate = PACKAGES.filter(pkg => {
      if (pkg === 'basic') return true;
      const p = formData.pricing[pkg];
      return p.title || p.description || p.deliveryDays || p.price;
    });

    packagesToValidate.forEach(pkg => {
      REQUIRED_FIELDS.forEach(field => {
        const val = formData.pricing[pkg][field];
        if (!val || String(val).trim() === '') {
          newErrors[`${pkg}-${field}`] = true;
        }
      });
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ── Build API payload ────────────────────────────────────────────────────────  // Build the payload the backend expects
  const buildPayload = (status = 'published') => {
    const pricing = {};
    PACKAGES.forEach(pkg => {
      const p = formData.pricing[pkg];
      // Only include this package if it has both a title and a price
      if (p.title && p.title.trim() !== '' && parseFloat(p.price) > 0) {
        pricing[pkg] = {
          name: pkg,
          title: p.title,
          description: p.description,
          deliveryTimeframe: p.deliveryDays,
          price: parseFloat(p.price),
        };
      }
    });

    const addons = formData.addons.map(addon => ({
      name: addon.name,
      values: {
        basic:    addon.selected[0] ? 'included' : '',
        standard: addon.selected[1] ? 'included' : '',
        advanced: addon.selected[2] ? 'included' : '',
        pro:      addon.selected[3] ? 'included' : '',
        premium:  addon.selected[4] ? 'included' : '',
      }
    }));

    return {
      title:           formData.title,
      description:     formData.description,
      category:        formData.selectedCategories,
      tags:            formData.keywords,
      hellocians:      formData.selectedHellocians,
      cover:           formData.images[0]?.preview || 'https://placehold.co/600x400',
      firstThumbnail:  formData.images[1]?.preview,
      secondThumbnail: formData.images[2]?.preview,
      thirdThumbnail:  formData.images[3]?.preview,
      pricing,
      addons,
      status,
    };
  };

  const { mutate: performSaveGig, isPending: isSaving } = useMutation({
    mutationFn: (status) => {
      const payload = buildPayload(status);
      return isEditMode && gigId ? updateGig(gigId, payload) : createGig(payload);
    },
    onSuccess: (_, status) => {
      const isDraft = status === 'draft';
      if (isEditMode) {
        toast.success('Gig updated successfully!');
      } else {
        toast.success(isDraft ? 'Draft saved successfully!' : 'Gig published successfully!');
        localStorage.removeItem('gigCreationData');
        localStorage.removeItem('gigCreationStep');
      }
      navigate('/admins/gigs');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to save gig');
    }
  });

  const handlePublish = () => {
    const isUploading = formData.images.some(img => img.loading);
    if (isUploading) {
      toast.warning('Please wait for images to finish uploading.');
      return;
    }
    if (validate()) {
      performSaveGig('published');
    } else {
      toast.error('Please fill in all required fields for the Basic package (and any other partially filled packages).');
    }
  };

  const handleSaveDraft = () => {
    const isUploading = formData.images.some(img => img.loading);
    if (isUploading) {
      toast.warning('Please wait for images to finish uploading.');
      return;
    }
    // Drafts don't need strict validation, but we need at least a title
    if (!formData.title) {
      toast.error('A title is required to save a draft.');
      return;
    }
    performSaveGig('draft');
  };

  // ── Pricing Handlers ─────────────────────────────────────────────────────────
  const handlePricingChange = (pkg, field, value) => {
    setFormData(prev => ({
      ...prev,
      pricing: {
        ...prev.pricing,
        [pkg]: { ...prev.pricing[pkg], [field]: value }
      }
    }));
    // Clear error for this field as user fills it
    if (errors[`${pkg}-${field}`]) {
      setErrors(prev => { const e = { ...prev }; delete e[`${pkg}-${field}`]; return e; });
    }
  };

  // ── Addon Handlers ───────────────────────────────────────────────────────────
  const addNewAddon = () => {
    setFormData(prev => ({
      ...prev,
      addons: [...prev.addons, { name: '', selected: [false, false, false, false, false] }]
    }));
  };

  const updateAddonName = (index, name) => {
    const newAddons = formData.addons.map((addon, ai) =>
      ai === index ? { ...addon, name } : addon
    );
    setFormData(prev => ({ ...prev, addons: newAddons }));
  };

  const toggleAddonSelection = (addonIndex, packageIndex) => {
    const newAddons = formData.addons.map((addon, ai) => {
      if (ai !== addonIndex) return addon;
      const selected = [...addon.selected];
      selected[packageIndex] = !selected[packageIndex];
      return { ...addon, selected };
    });
    setFormData(prev => ({ ...prev, addons: newAddons }));
  };

  const deleteAddon = (addonIndex) => {
    setFormData(prev => ({
      ...prev,
      addons: prev.addons.filter((_, i) => i !== addonIndex)
    }));
  };

  // ── Cell Renderer ─────────────────────────────────────────────────────────────
  const renderCell = (pkg, field, pkgIndex) => {
    const isRequired = pkg === 'basic';
    const hasError = !!errors[`${pkg}-${field}`];
    const value = formData.pricing[pkg][field];
    const isEditing = editingField === `${pkg}-${field}`;

    const borderClass = hasError
      ? 'border-red-400 ring-1 ring-red-400'
      : isEditing
      ? 'border-blue-500 ring-1 ring-blue-500'
      : 'border-transparent';

    return (
      <td
        key={pkg}
        className={`p-2 relative ${pkgIndex < 4 ? 'border-r border-gray-300 dark:border-[#32353c]' : ''}`}
      >
        <div
          className={`relative cursor-pointer rounded border ${borderClass} transition-all duration-200`}
          onClick={() => setEditingField(`${pkg}-${field}`)}
        >
          {isEditing ? (
            field === 'description' ? (
              <textarea
                className="w-full p-2 rounded text-sm focus:outline-none bg-transparent"
                value={value}
                placeholder={`${pkg} description`}
                onChange={e => handlePricingChange(pkg, field, e.target.value)}
                onBlur={() => setEditingField(null)}
                rows={3}
                autoFocus
              />
            ) : (
              <input
                type={field === 'price' ? 'number' : 'text'}
                min={field === 'price' ? '0' : undefined}
                className="w-full p-2 rounded text-sm focus:outline-none bg-transparent"
                value={value}
                placeholder={field === 'price' ? '0.00' : `${pkg} ${field}`}
                onChange={e => handlePricingChange(pkg, field, e.target.value)}
                onBlur={() => setEditingField(null)}
                autoFocus
              />
            )
          ) : (
            <div className={`text-sm p-2 min-h-[2rem] flex items-start justify-between gap-1 transition-colors ${value ? 'text-gray-700 dark:text-gray-300' : 'text-gray-300 dark:text-gray-600 italic'}`}>
              <span className="flex-1 break-words">
                {value || (field === 'price' ? 'Set price' : field === 'deliveryDays' ? 'Set timeframe' : `Add ${field}`)}
              </span>
              <svg className="w-3 h-3 mt-0.5 flex-shrink-0 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </div>
          )}
        </div>
        {hasError && (
          <p className="text-red-500 text-xs mt-0.5 px-1">Required</p>
        )}
      </td>
    );
  };

  return (
    <div className="bg-white dark:bg-[#191c22] p-6 rounded-lg shadow-md border dark:border-[#32353c] transition-colors duration-200">
      {/* Legend */}
      <div className="flex items-center gap-4 mb-4 text-xs text-gray-500 dark:text-gray-400">
        <span className="flex items-center gap-1">
          <span className="inline-block w-2 h-2 rounded-full bg-[#174568] dark:bg-blue-600"></span>
          Basic package — required
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-2 h-2 rounded-full bg-gray-300 dark:bg-[#32353c]"></span>
          Other packages — optional (fill all fields or none)
        </span>
      </div>

      {/* Pricing Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse border border-gray-300 dark:border-[#32353c] text-sm">
          <thead>
            <tr className="border-b dark:border-[#32353c] bg-gray-50 dark:bg-[#0b0e14]">
              <th className="p-3 text-left border-r border-gray-300 dark:border-[#32353c] w-36"></th>
              {PACKAGES.map((pkg, i) => (
                <th
                  key={pkg}
                  className={`p-3 text-center capitalize transition-colors ${i < 4 ? 'border-r border-gray-300 dark:border-[#32353c]' : ''} ${pkg === 'basic' ? 'text-[#174568] dark:text-blue-400 font-bold' : 'text-gray-600 dark:text-gray-400 font-medium'}`}
                >
                  {pkg}
                  {pkg === 'basic' && <span className="text-red-500 ml-0.5">*</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {REQUIRED_FIELDS.map(field => (
              <tr key={field} className="border-b dark:border-[#32353c]">
                <td className="p-3 text-left border-r border-gray-300 dark:border-[#32353c] bg-gray-50 dark:bg-[#0b0e14] font-medium whitespace-nowrap dark:text-gray-300">
                  {FIELD_LABELS[field]}
                  <span className="text-red-500 ml-0.5">*</span>
                </td>
                {PACKAGES.map((pkg, pkgIndex) => renderCell(pkg, field, pkgIndex))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Validation summary */}
      {Object.keys(errors).length > 0 && (
        <div className="mt-3 p-3 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900/50 rounded-md text-sm text-red-600 dark:text-red-400">
          <strong>Missing required fields:</strong>{' '}
          {[...new Set(Object.keys(errors).map(k => {
            const [pkg, field] = k.split('-');
            return `${pkg.charAt(0).toUpperCase() + pkg.slice(1)} → ${FIELD_LABELS[field] || field}`;
          }))].join(', ')}
        </div>
      )}

      {/* Add-ons Section */}
      <div className="mt-8">
        <div className="flex items-center mb-4">
          <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200">Addons</h3>
        </div>
 
        <table className="w-full border-collapse border border-gray-300 dark:border-[#32353c] text-sm">
          <tbody>
            {formData.addons.map((addon, addonIndex) => (
              <tr key={addonIndex} className="border-b dark:border-[#32353c]">
                <td className="p-3 w-1/3 border-r border-gray-300 dark:border-[#32353c]">
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      {addonIndex === 0 ? (
                        <div className="text-gray-700 dark:text-gray-300">{addon.name}</div>
                      ) : (
                        <input
                          type="text"
                          className="w-full border border-gray-300 dark:border-[#32353c] p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-[#0b0e14] dark:text-white transition-colors"
                          value={addon.name}
                          placeholder="Enter the addon text"
                          onChange={e => updateAddonName(addonIndex, e.target.value)}
                        />
                      )}
                    </div>
                    {addonIndex > 0 && (
                      <button
                        className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                        onClick={() => deleteAddon(addonIndex)}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                </td>
                {addon.selected.map((isSelected, pkgIndex) => (
                  <td key={pkgIndex} className={`p-3 text-center ${pkgIndex < 4 ? 'border-r border-gray-300 dark:border-[#32353c]' : ''}`}>
                    <div
                      className={`w-5 h-5 mx-auto cursor-pointer border-2 rounded transition-colors ${isSelected ? 'bg-[#174568] dark:bg-blue-600 border-[#174568] dark:border-blue-600' : 'bg-white dark:bg-[#0b0e14] border-gray-300 dark:border-[#32353c] hover:border-gray-400 dark:hover:border-gray-500'} flex items-center justify-center`}
                      onClick={() => toggleAddonSelection(addonIndex, pkgIndex)}
                    >
                      {isSelected && (
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>

        <button
          className="mt-3 px-4 py-2 bg-gray-100 dark:bg-[#1d2026] text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-[#32353c] rounded-md flex items-center text-sm border border-gray-200 dark:border-[#32353c] transition-colors"
          onClick={addNewAddon}
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Add New Addon
        </button>
      </div>

      {/* Action Buttons */}
      <div className="mt-8 flex justify-between">
        <button
          className="px-6 py-2 bg-gray-200 dark:bg-[#1d2026] text-gray-800 dark:text-white hover:bg-gray-300 dark:hover:bg-[#32353c] rounded-md flex items-center transition-colors"
          onClick={() => setCurrentStep(2)}
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back
        </button>

        <div className="flex gap-3">
          <button
            className="px-6 py-2 bg-gray-200 dark:bg-[#1d2026] text-gray-800 dark:text-white hover:bg-gray-300 dark:hover:bg-[#32353c] rounded-md text-sm disabled:opacity-50 transition-colors"
            onClick={handleSaveDraft}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save as Draft'}
          </button>
          <button
            className="px-6 py-2 bg-[#174568] dark:bg-blue-600 text-white hover:bg-[#0f3350] dark:hover:bg-blue-700 rounded-md flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            onClick={handlePublish}
            disabled={isSaving}
          >
            {isSaving && (
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
            )}
            {isSaving ? 'Publishing...' : 'Publish'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Step3;